import { isEmpty } from 'lodash';
import {
  Call,
  ApiUrl,
  PageParams,
  ParseGeneralResponse,
  setObjectToFormData,
  linehaulJavaUrl,
} from './ApiUtils';
import { alertMessage } from '../common/Common';

const laneDetailsInflight = new Map();

export const LineHaulApi = {
  fetch: (isPagingEnabled, order_id, page = 1, perPage = '', token = '', payload = {}) => {
    let params = '';
    if (!isEmpty(token)) {
      params = `&order_no=${token}`;
    } else {
      const parameter = Object.keys(payload).map(
        (key) => `${key}=${!isEmpty(payload[ key ]) ? payload[ key ] : ''}`,
      );
      params = parameter ? `&${parameter.join('&')}` : '';
    }

    if (isPagingEnabled) {
      const url = ApiUrl(
        `v2/line_haul?${PageParams(
          page,
          perPage,
        )}${params}&operation_code=OREI&customer_order_id=${order_id}`,
      );
      return Call('get', url, '', ParseGeneralResponse, {
        responseDataKeys: {
          linehauls: 'linehauls',
          pagination: 'meta.pagination',
          stats: 'stats',
        },
      });
    } else {
      const url = ApiUrl(
        `v2/line_haul?page=1&per_page=all${params}&operation_code=OREI&customer_order_id=${order_id}`,
      );
      return Call('get', url, '', ParseGeneralResponse, {
        responseDataKeys: {
          linehauls: 'linehauls',
          pagination: 'meta.pagination',
          stats: 'stats',
        },
      });
    }
  },

  fetchOne:(id) => {
    const url = ApiUrl('v2/line_haul/get_order_line_haul?customer_order_id=' +id)
    return Call('get', url, {} , ParseGeneralResponse, {
      responseDataKeys : {linehaulstops: 'linehaul'}
    })
  },

  create: (payload) => {
    const url = ApiUrl('v2/line_haul')
    const formData = setObjectToFormData(payload)
    return Call('post', url, formData, ParseGeneralResponse, {
      responseDataKeys: { linehaul: 'linehaul' },
    })
  },

  update: (id, payload) => {
    const url = ApiUrl('v2/line_haul/' + id)
    const formData = setObjectToFormData(payload)
    return Call('put', url, formData, ParseGeneralResponse, {
      responseDataKeys: { linehaul: 'linehaul' },
    })
  },

  get:(id) => {
    const url = ApiUrl('v2/line_haul/' + id)
    return Call('get', url, {} , ParseGeneralResponse,{
      responseDataKeys : {linehaul: ''}
    })
  },
  deleteLh: (id) => {
    const url = ApiUrl('v2/line_haul/' + id)
    return Call('delete', url, {} , ParseGeneralResponse, {
      responseDataKeys: { linehaul: '' },
    })
  },
  createShipment: (payload) => {
    const url = ApiUrl('v2/line_haul/create_shipment')
    const formData = setObjectToFormData(payload)
    return Call('post', url, formData, ParseGeneralResponse, {
      responseDataKeys: { shipment: 'shipment' , errors : 'errors'},
    })
  },
  createRoute: (payload) => {
    const url = ApiUrl('v2/line_haul_routes/create_route')
    const formData = setObjectToFormData(payload)
    return Call('post', url, formData, ParseGeneralResponse, {
      responseDataKeys: { route: 'route' , errors : 'errors'},
    })
  },
  deleteRoute: (id) => {
    const url = ApiUrl('v2/line_haul_routes/' + id)
    return Call('delete', url, {} , ParseGeneralResponse, {
      responseDataKeys: { route: '' },
    })
  },
  bulkDelete: (ids) => {
    const url = ApiUrl('v2/line_haul/bulk_delete_linehauls')
    return Call('delete', url, { linehaul_ids: ids }, ParseGeneralResponse, {
      responseDataKeys: { data: '' },
    })
  },
  bulkSchedule: (payload) => {
    const url = ApiUrl('v2/line_haul/bulk_schedule_linehauls')
    return Call('put', url, payload , ParseGeneralResponse, {
      responseDataKeys: { data: '' },
    })
  },
  changeStatus: (payload) => {
    const url = ApiUrl(`v2/line_haul_orders/update_bulk_linehaul_status`)
    return Call('put', url, payload , ParseGeneralResponse, {
      responseDataKeys: { data: '' },
    })
  },
  fetchLineHaulLanes: (organization_id, origin_warehouse_id, destination_warehouse_id) => {
    const url = ApiUrl(`v2/linehaul_routing_paths/search?organization_id=${organization_id}&origin_warehouse_id=${origin_warehouse_id}&destination_warehouse_id=${destination_warehouse_id}`);
    return Call('get', url, {}, ParseGeneralResponse, {
      responseDataKeys: { routing_paths: 'routing_paths' },
    });
  },
  
  fetchLineHaulLanesJava: (organization_id, origin_warehouse_id, destination_warehouse_id) => {
    if (
      origin_warehouse_id &&
      destination_warehouse_id &&
      String(origin_warehouse_id) === String(destination_warehouse_id)
    ) {
      return Promise.resolve({ success: false, data: { paths: [] } });
    }

    const queryParams = new URLSearchParams({
      organization_id,
      origin_warehouse_id,
      destination_warehouse_id,
    });
    const cacheKey = `${organization_id}|${origin_warehouse_id}|${destination_warehouse_id}`;
    if (laneDetailsInflight.has(cacheKey)) {
      return laneDetailsInflight.get(cacheKey);
    }

    const url = linehaulJavaUrl(`v1/linehaul/routing-paths/lane-details?${queryParams}`);
    const token = JSON.parse(localStorage.getItem('auth') || '{}').access_token;

    const request = fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }).then(async (response) => {
      const data = await response.json();
      if (!response.ok) {
        alertMessage(data?.message || data?.error || 'Failed to fetch lane details', 'warning');
        return { success: false, message: data?.message || data?.error || 'Failed to fetch lane details' };
      }
      return data;
    }).catch((err) => {
      throw err;
    }).finally(() => {
      laneDetailsInflight.delete(cacheKey);
    });

    laneDetailsInflight.set(cacheKey, request);
    return request;
  },

  fetchLineHaulRealTimeTracking: (orderId) => {
    const queryParams = new URLSearchParams({
      order_id: orderId,
    });
    const url = linehaulJavaUrl(`v1/customer-orders/linehaul-tracking?${queryParams}`);
    const token = JSON.parse(localStorage.getItem('auth') || '{}').access_token;

    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }).then(async (response) => {
      const data = await response.json();
      if (!response.ok) {
        // 404 means tracking has not been created yet — empty state, not a toast.
        if (response.status !== 404) {
          alertMessage(data?.message || data?.error || 'Failed to fetch tracking details', 'error');
        }
        return { success: false, message: data?.message || data?.error || 'Failed to fetch tracking details' };
      }
      return data;
    });
  },

  fetchWarehousesByZipcode: (organization_id, origin_zipcode , destination_zipcode) => {
    const url = ApiUrl(`v2/locations/warehouses_by_zipcode?organization_id=${organization_id}&origin_zipcode=${origin_zipcode}&destination_zipcode=${destination_zipcode}`);
    return Call('get', url, {}, ParseGeneralResponse, {
      responseDataKeys: { data: '' },
    });
  },

  saveLinehaulRoutingLane: (payload) => {
    const url = ApiUrl('v2/customer_orders/save_linehaul_routing_lane');
    const formData = setObjectToFormData(payload);
    return Call('put', url, formData, ParseGeneralResponse, {
      responseDataKeys: { data: '' , errors : 'errors'},
    });
  },

  updateLinehaulRoutingLane: (payload) => {
    const url = ApiUrl('v2/customer_orders/update_linehaul_routing_lane');
    const formData = setObjectToFormData(payload);
    return Call('put', url, formData, ParseGeneralResponse, {
      responseDataKeys: { data: '' , errors : 'errors'},
    });
  },

};


